import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiSave,
  FiBox,
  FiDollarSign,
  FiClock,
  FiImage,
  FiUploadCloud,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiLayers,
  FiTrash2,
  FiPlus,
  FiMapPin,
  FiX,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import RichTextEditor from "../components/RichTextEditor";
import Cropper from "react-easy-crop";
import ResponsiveModal from "../components/ResponsiveModal";
import BreadcrumbTrail from "../components/BreadcrumbTrail";
import EmptyState from "../components/EmptyState";
import { useCompany } from "../hooks/useCompany";
import { extractList } from "../utils/api";


const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
];

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedBlob = async (imageSrc, pixelCrop, outputType) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      outputType,
      outputType === "image/png" ? undefined : 0.92,
    );
  });
};

const NewProductPage = () => {
  const navigate = useNavigate();
  const { id: productId } = useParams();
  const { user } = useAuth();
  const { company, loading: companyLoading } = useCompany();
  const isEditing = Boolean(productId);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState({});
  const [selectedImageSrc, setSelectedImageSrc] = useState("");
  const [selectedImageName, setSelectedImageName] = useState("produto");
  const [selectedImageType, setSelectedImageType] = useState("image/jpeg");
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    categoryId: "",
    description: "",
    coverageStates: [],
    coverageCities: [],
    priceCondition: "",
    hasUrgency: false,
    media: [],
  });

  const [ibgeStates, setIbgeStates] = useState([]);
  const [ibgeStatesLoading, setIbgeStatesLoading] = useState(false);
  const [ibgeCitiesCache, setIbgeCitiesCache] = useState({});
  const [ibgeCitiesLoading, setIbgeCitiesLoading] = useState({});
  const [stateSearch, setStateSearch] = useState("");
  const [citySearches, setCitySearches] = useState({});
  const [coverageCityMode, setCoverageCityMode] = useState({});
  const [cityDropdownOpen, setCityDropdownOpen] = useState({});

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await axiosInstance.get("/categories", {
          params: { grouped: true, isActive: true },
        });
        setCategories(response.data?.data?.data || response.data?.data || []);
      } catch (fetchError) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      setIbgeStatesLoading(true);
      try {
        const res = await fetch(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome"
        );
        const data = await res.json();
        setIbgeStates(data);
      } catch {
        setIbgeStates([]);
      } finally {
        setIbgeStatesLoading(false);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!isEditing || !productId) return;

    const fetchProduct = async () => {
      setProductLoading(true);
      setError("");

      try {
        const response = await axiosInstance.get(`/products/${productId}`);
        const product = response?.data?.data || response?.data;
        const media = Array.isArray(product?.media) ? product.media : [];
        const mediaUrls = Array.isArray(product?.mediaUrls)
          ? product.mediaUrls
          : [];

        setMediaPreviewUrls(
          media.reduce((accumulator, mediaItem, index) => {
            const mediaUrl = mediaUrls[index];

            if (!mediaUrl) return accumulator;

            accumulator[mediaItem] = mediaUrl;
            return accumulator;
          }, {}),
        );

        const loadedStates = Array.isArray(product?.coverageStates) ? product.coverageStates : [];
        const loadedCities = Array.isArray(product?.coverageCities) ? product.coverageCities : [];

        setFormData({
          title: product?.title || "",
          categoryId: product?.categoryId ? String(product.categoryId) : "",
          description: product?.description || "",
          coverageStates: loadedStates,
          coverageCities: loadedCities,
          priceCondition: product?.priceCondition || "",
          hasUrgency: Boolean(product?.hasUrgency),
          media,
        });

        // Busca cidades de cada estado salvo e detecta modo
        for (const sigla of loadedStates) {
          setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: true }));
          fetch(
            `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}/municipios?orderBy=nome`
          )
            .then((r) => r.json())
            .then((data) => {
              setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: data }));
              const hasSpecific = data.some((c) => loadedCities.includes(c.nome));
              setCoverageCityMode((prev) => ({
                ...prev,
                [sigla]: hasSpecific ? "specific" : "all",
              }));
            })
            .catch(() => setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: [] })))
            .finally(() =>
              setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: false }))
            );
        }
      } catch (fetchError) {
        setError("Não foi possível carregar o produto para edição.");
      } finally {
        setProductLoading(false);
      }
    };

    fetchProduct();
  }, [isEditing, productId]);

  useEffect(() => {
    return () => {
      Object.values(mediaPreviewUrls).forEach((previewUrl) => {
        if (typeof previewUrl === "string" && previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    };
  }, [mediaPreviewUrls]);

  const selectedCategoryName = useMemo(() => {
    for (const parent of categories) {
      if (String(parent.id) === String(formData.categoryId)) {
        return parent.nome;
      }
      const sub = parent.subcategories?.find(
        (s) => String(s.id) === String(formData.categoryId)
      );
      if (sub) return `${parent.nome} › ${sub.nome}`;
    }
    return "Não selecionada";
  }, [categories, formData.categoryId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchCitiesForState = async (sigla) => {
    if (ibgeCitiesCache[sigla] !== undefined) return;
    setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: true }));
    try {
      const res = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}/municipios?orderBy=nome`
      );
      const data = await res.json();
      setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: data }));
    } catch {
      setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: [] }));
    } finally {
      setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: false }));
    }
  };

  const toggleStateSelection = (sigla) => {
    const isSelected = formData.coverageStates.includes(sigla);
    if (isSelected) {
      const stateCityNames = (ibgeCitiesCache[sigla] || []).map((c) => c.nome);
      setFormData((prev) => ({
        ...prev,
        coverageStates: prev.coverageStates.filter((s) => s !== sigla),
        coverageCities: prev.coverageCities.filter((c) => !stateCityNames.includes(c)),
      }));
      setCoverageCityMode((prev) => { const next = { ...prev }; delete next[sigla]; return next; });
      setCitySearches((prev) => { const next = { ...prev }; delete next[sigla]; return next; });
    } else {
      setFormData((prev) => ({
        ...prev,
        coverageStates: [...prev.coverageStates, sigla],
      }));
      setCoverageCityMode((prev) => ({ ...prev, [sigla]: "all" }));
      fetchCitiesForState(sigla);
    }
  };

  const toggleCitySelection = (cityName) => {
    setFormData((prev) => ({
      ...prev,
      coverageCities: prev.coverageCities.includes(cityName)
        ? prev.coverageCities.filter((c) => c !== cityName)
        : [...prev.coverageCities, cityName],
    }));
  };

  const handlePriceChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value) {
      value = (parseInt(value, 10) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    }
    setFormData((prev) => ({
      ...prev,
      priceCondition: value,
    }));
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const data = new FormData();
    data.append("image", file);

    try {
      const response = await axiosInstance.post("/upload/product", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const imageUrl =
        response?.data?.imageUrl || response?.data?.imageName || "";

      if (imageUrl) {
        const previewUrl = URL.createObjectURL(file);

        setFormData((prev) => ({
          ...prev,
          media: [...prev.media, imageUrl],
        }));
        setMediaPreviewUrls((prev) => ({
          ...prev,
          [imageUrl]: previewUrl,
        }));
      } else {
        throw new Error("Resposta de upload sem URL da imagem");
      }
    } catch (err) {
      console.error("Erro no upload, usando base64 fallback", err);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          media: [...prev.media, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const closeCropper = () => {
    setIsCropOpen(false);
    setSelectedImageSrc("");
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    const hasValidExtension = ["jpg", "jpeg", "png", "webp"].includes(
      extension,
    );
    const hasValidMimeType = ALLOWED_IMAGE_TYPES.includes(file.type);

    if (!hasValidExtension || !hasValidMimeType) {
      setError(
        "Formato inválido. Envie apenas imagens JPG, JPEG, PNG ou WEBP.",
      );
      return;
    }

    setError("");
    setSelectedImageName(file.name.replace(/\.[^/.]+$/, "") || "produto");
    setSelectedImageType(
      file.type === "image/png"
        ? "image/png"
        : file.type === "image/webp"
          ? "image/webp"
          : "image/jpeg",
    );

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImageSrc(reader.result);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const confirmCropAndUpload = async () => {
    if (!selectedImageSrc || !croppedAreaPixels) return;

    setError("");

    try {
      const croppedBlob = await getCroppedBlob(
        selectedImageSrc,
        croppedAreaPixels,
        selectedImageType,
      );

      if (!croppedBlob) {
        setError(
          "Nao foi possivel processar o recorte da imagem. Tente novamente.",
        );
        return;
      }

      const extension =
        selectedImageType === "image/png"
          ? "png"
          : selectedImageType === "image/webp"
            ? "webp"
            : "jpg";
      const croppedFile = new File(
        [croppedBlob],
        `${selectedImageName}-crop.${extension}`,
        { type: selectedImageType },
      );

      closeCropper();
      await uploadFile(croppedFile);
    } catch (err) {
      console.error("Erro ao recortar imagem", err);
      setError("Nao foi possivel recortar a imagem. Tente novamente.");
    }
  };

  const removeMedia = (index) => {
    setFormData((prev) => {
      const mediaItem = prev.media[index];

      setMediaPreviewUrls((currentPreviewUrls) => {
        const previewUrl = currentPreviewUrls[mediaItem];

        if (previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }

        return Object.fromEntries(
          Object.entries(currentPreviewUrls).filter(([key]) => key !== mediaItem),
        );
      });

      return {
        ...prev,
        media: prev.media.filter((_, i) => i !== index),
      };
    });
  };

  const resolveMediaPreviewSrc = (mediaItem) => {
    if (!mediaItem) return "";
    if (typeof mediaItem !== "string") return "";
    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }

    return mediaPreviewUrls[mediaItem] || `https://relinq.s3.sa-east-1.amazonaws.com/${mediaItem}`;
  };

  const handleSubmit = async () => {
    if (!formData.categoryId) {
      setError("Selecione a categoria do produto para continuar.");
      setStep(1);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        title: formData.title,
        categoryId: formData.categoryId,
        description: formData.description,
        coverageStates: formData.coverageStates,
        coverageCities: formData.coverageCities,
        priceCondition: formData.priceCondition,
        hasUrgency: formData.hasUrgency,
        media: formData.media,
      };

      if (isEditing) {
        await axiosInstance.put(`/products/${productId}`, payload);
      } else {
        await axiosInstance.post("/products", payload);
      }

      navigate("/produtos");
    } catch (err) {
      console.error(err);
      setError(
        isEditing
          ? "Ocorreu um erro ao atualizar o produto. Verifique os dados e tente novamente."
          : "Ocorreu um erro ao submeter o catálogo. Verifique os dados e tente novamente.",
      );
    } finally {
      setLoading(false);  
    }
  };

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const isStep1Valid =
    formData.title.trim().length > 0 && String(formData.categoryId).trim().length > 0;

  // Steps visual configuration
  const steps = [
    { num: 1, label: "Conteúdo" },
    { num: 2, label: "Condições" },
    { num: 3, label: "Revisão Final" },
  ];

  const breadcrumbItems = [
    { label: "Produtos", to: user?.role === "ADMIN" ? "/admin/produtos" : "/produtos" },
    { label: isEditing ? "Editar produto" : "Novo produto" },
  ];

  if (companyLoading || productLoading) {
    return <div className="muted">Carregando dados da empresa...</div>;
  }

  if (!isEditing && company?.statusVerificacao !== "APROVADO") {
    return (
      <div className="fade-in page-stack">
        <EmptyState
          icon={FiBox}
          title="Cadastro da empresa não aprovado"
          description="Você só pode cadastrar produtos quando o status da empresa estiver aprovado."
          actionLabel="Ir para Minha Empresa"
          onAction={() => navigate("/empresa")}
        />
      </div>
    );
  }

  return (
    <div className="fade-in page-stack">
      <div className="page-header" style={{ alignItems: "center" }}>
        <div style={{ width: "100%" }}>
          <BreadcrumbTrail items={breadcrumbItems} />
        </div>
      </div>

      <div className="card card--outlined w-full" style={{ margin: "0 auto" }}>
        {/* Wizard Progress Bar */}
        <div style={{ marginBottom: "1.75rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0",
            }}
          >
            {steps.map((s, i) => {
              const isActive = step === s.num;
              const isCompleted = step > s.num;

              return (
                <div
                  key={s.num}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: i < steps.length - 1 ? 1 : "none",
                  }}
                >
                  {/* Step pill */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "0.35rem 0.75rem 0.35rem 0.35rem",
                      borderRadius: "999px",
                      backgroundColor: isCompleted
                        ? "#dcfce7"
                        : isActive
                        ? "#166534"
                        : "#f1f5f9",
                      border: isActive
                        ? "none"
                        : isCompleted
                        ? "1px solid #bbf7d0"
                        : "1px solid #e2e8f0",
                      transition: "all 0.25s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isCompleted
                          ? "#16a34a"
                          : isActive
                          ? "rgba(255,255,255,0.2)"
                          : "#e2e8f0",
                        color: isCompleted
                          ? "#fff"
                          : isActive
                          ? "#fff"
                          : "#94a3b8",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {isCompleted ? <FiCheck size={12} /> : s.num}
                    </div>
                    <span
                      className={isActive ? undefined : "wizard-step-label--inactive"}
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: isCompleted
                          ? "#166534"
                          : isActive
                          ? "#fff"
                          : "#94a3b8",
                      }}
                    >
                      {s.label}
                    </span>
                  </div>

                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: "2px",
                        backgroundColor: isCompleted ? "#86efac" : "#e2e8f0",
                        margin: "0 4px",
                        borderRadius: "999px",
                        transition: "background-color 0.3s",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "var(--color-danger-light, #fee2e2)",
              color: "var(--color-danger, #ef4444)",
              padding: "1rem",
              borderRadius: "0.5rem",
              marginBottom: "1.5rem",
              fontWeight: "500",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ minHeight: "350px", position: "relative" }}>
          {/* Sessão 1: Informações Gerais e Mídia */}
          {step === 1 && (
            <div className="fade-in">
              <h3
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "1.25rem",
                  marginBottom: "1.5rem",
                }}
              >
                <FiBox color="#237e22" /> Que lote quer cadastrar?
              </h3>

              <div
                className="form-group"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                {/* Midia Integrada ao Passo 1 */}
                <div>
                  <p
                    style={{
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      marginBottom: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      color: "#1f2937",
                    }}
                  >
                    <FiImage size={16} color="#237e22" /> Fotos do produto
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    {formData.media.map((img, idx) => (
                      <div
                        key={idx}
                        style={{
                          position: "relative",
                          borderRadius: "0.6rem",
                          overflow: "hidden",
                          border: "1px solid var(--color-border)",
                          aspectRatio: "1 / 1",
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <img
                          src={resolveMediaPreviewSrc(img)}
                          alt={`Foto ${idx + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeMedia(idx)}
                          aria-label="Remover imagem"
                          style={{
                            position: "absolute",
                            top: "6px",
                            right: "6px",
                            background: "rgba(0,0,0,0.55)",
                            backdropFilter: "blur(4px)",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "26px",
                            height: "26px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiTrash2 size={13} />
                        </button>
                        {idx === 0 && (
                          <span
                            style={{
                              position: "absolute",
                              bottom: "6px",
                              left: "6px",
                              background: "#237e22",
                              color: "#fff",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: "999px",
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                            }}
                          >
                            Capa
                          </span>
                        )}
                      </div>
                    ))}

                    {/* Botão adicionar */}
                    <label
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.4rem",
                        aspectRatio: "1 / 1",
                        border: "2px dashed #237e22",
                        borderRadius: "0.6rem",
                        backgroundColor: uploading ? "#f0fdf4" : "#f8fffe",
                        cursor: uploading ? "not-allowed" : "pointer",
                        opacity: uploading ? 0.7 : 1,
                        transition: "background 0.2s",
                        minHeight: "100px",
                      }}
                    >
                      {uploading ? (
                        <FiUploadCloud size={22} color="#237e22" />
                      ) : (
                        <FiPlus size={22} color="#237e22" />
                      )}
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#237e22",
                          textAlign: "center",
                          lineHeight: 1.3,
                        }}
                      >
                        {uploading ? "Enviando..." : "Adicionar foto"}
                      </span>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="title"
                    style={{
                      fontWeight: "600",
                      display: "block",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Título do produto{" "}
                    <span style={{ color: "var(--color-danger)" }}>*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    placeholder="Ex: Lote Especial de Café Arábica, Máquinas XPTO..."
                    className="input"
                    value={formData.title}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="categoryId"
                    style={{
                      fontWeight: "600",
                      display: "block",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Categoria do produto{" "}
                    <span style={{ color: "var(--color-danger)" }}>*</span>
                  </label>
                  <select
                    id="categoryId"
                    name="categoryId"
                    className="select"
                    value={formData.categoryId}
                    onChange={handleChange}
                    required
                    disabled={categoriesLoading}
                  >
                    <option value="">
                      {categoriesLoading
                        ? "Carregando categorias..."
                        : "Selecione uma categoria"}
                    </option>
                    {categories.map((parent) =>
                      parent.subcategories?.length > 0 ? (
                        <optgroup key={parent.id} label={parent.nome}>
                          {parent.subcategories.map((sub) => (
                            <option key={sub.id} value={sub.id}>
                              {sub.nome}
                            </option>
                          ))}
                        </optgroup>
                      ) : (
                        <option key={parent.id} value={parent.id}>
                          {parent.nome}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="description"
                    style={{
                      fontWeight: "600",
                      display: "block",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Descrição do produto
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, description: val }))
                    }
                    placeholder="Descreva detalhes como origem, qualidade e diferenciais competitivos..."
                  />
                </div>

                {/* Abrangência geográfica */}
                <div>
                  <label
                    style={{
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      marginBottom: "0.35rem",
                    }}
                  >
                    <FiMapPin size={16} color="#237e22" /> Abrangência geográfica
                  </label>
                  <p style={{ fontSize: "0.82rem", color: "var(--ink-500, #6b5c4e)", marginBottom: "0.75rem" }}>
                    Selecione os estados e depois as cidades onde este produto está disponível.
                  </p>

                  {/* Busca de estados */}
                  <input
                    className="input"
                    placeholder="Buscar estado..."
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    style={{ marginBottom: "0.75rem" }}
                  />

                  {/* Grid de estados */}
                  {ibgeStatesLoading ? (
                    <p style={{ fontSize: "0.85rem", color: "var(--ink-500)" }}>Carregando estados...</p>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                        gap: "8px",
                        padding: "2px",
                      }}
                    >
                      {ibgeStates
                        .filter((s) =>
                          s.nome.toLowerCase().includes(stateSearch.toLowerCase()) ||
                          s.sigla.toLowerCase().includes(stateSearch.toLowerCase())
                        )
                        .map((state) => {
                          const selected = formData.coverageStates.includes(state.sigla);
                          return (
                            <button
                              key={state.id}
                              type="button"
                              onClick={() => toggleStateSelection(state.sigla)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 12px",
                                borderRadius: "10px",
                                border: selected ? "2px solid #166534" : "1px solid #e2e8f0",
                                backgroundColor: selected ? "#f0fdf4" : "#fafafa",
                                cursor: "pointer",
                                textAlign: "left",
                                transition: "all 0.15s",
                              }}
                            >
                              <span
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  backgroundColor: selected ? "#166534" : "#e2e8f0",
                                  color: selected ? "#fff" : "#64748b",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.68rem",
                                  fontWeight: 800,
                                  flexShrink: 0,
                                  letterSpacing: "0.04em",
                                }}
                              >
                                {state.sigla}
                              </span>
                              <span
                                style={{
                                  fontSize: "0.82rem",
                                  fontWeight: selected ? 600 : 400,
                                  color: selected ? "#166534" : "#374151",
                                  lineHeight: 1.2,
                                }}
                              >
                                {state.nome}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  )}

                  {/* Seleção de cidades por estado */}
                  {formData.coverageStates.length > 0 && (
                    <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <p style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1f2937", margin: 0 }}>
                        Cidades por estado
                      </p>
                      {formData.coverageStates.map((sigla) => {
                        const stateInfo = ibgeStates.find((s) => s.sigla === sigla);
                        const cities = ibgeCitiesCache[sigla] || [];
                        const isLoadingCities = ibgeCitiesLoading[sigla];
                        const mode = coverageCityMode[sigla] || "all";
                        const inputValue = citySearches[sigla] || "";
                        const isDropdownOpen = cityDropdownOpen[sigla] || false;
                        const selectedCities = formData.coverageCities.filter((c) =>
                          cities.some((city) => city.nome === c)
                        );
                        const suggestions = cities
                          .filter(
                            (c) =>
                              c.nome.toLowerCase().includes(inputValue.toLowerCase()) &&
                              !formData.coverageCities.includes(c.nome)
                          )
                          .slice(0, 8);

                        return (
                          <div
                            key={sigla}
                            style={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}
                          >
                            {/* Cabeçalho */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "10px 14px",
                                backgroundColor: "#f0fdf4",
                                borderBottom: "1px solid #e2e8f0",
                                borderRadius: "12px 12px 0 0",
                              }}
                            >
                              <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#166534" }}>
                                {stateInfo?.nome} ({sigla})
                              </span>
                              {mode === "specific" && selectedCities.length > 0 && (
                                <span style={{ fontSize: "0.78rem", color: "#166534" }}>
                                  {selectedCities.length} cidade{selectedCities.length > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>

                            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                              {/* Radio buttons */}
                              <div style={{ display: "flex", gap: "1.5rem" }}>
                                {[
                                  { value: "all", label: "Todas as cidades" },
                                  { value: "specific", label: "Cidades específicas" },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      cursor: "pointer",
                                      fontSize: "0.85rem",
                                      fontWeight: mode === opt.value ? 600 : 400,
                                      color: mode === opt.value ? "#166534" : "#374151",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`city-mode-${sigla}`}
                                      value={opt.value}
                                      checked={mode === opt.value}
                                      onChange={() => {
                                        setCoverageCityMode((prev) => ({ ...prev, [sigla]: opt.value }));
                                        if (opt.value === "all") {
                                          const stateCityNames = cities.map((c) => c.nome);
                                          setFormData((prev) => ({
                                            ...prev,
                                            coverageCities: prev.coverageCities.filter(
                                              (c) => !stateCityNames.includes(c)
                                            ),
                                          }));
                                          setCitySearches((prev) => ({ ...prev, [sigla]: "" }));
                                        }
                                      }}
                                      style={{ accentColor: "#166534" }}
                                    />
                                    {opt.label}
                                  </label>
                                ))}
                              </div>

                              {/* Autocomplete de cidades */}
                              {mode === "specific" && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {isLoadingCities ? (
                                    <p style={{ fontSize: "0.82rem", color: "var(--ink-500)", margin: 0 }}>
                                      Carregando cidades...
                                    </p>
                                  ) : (
                                    <div style={{ position: "relative" }}>
                                      <input
                                        className="input"
                                        placeholder={`Buscar cidade em ${stateInfo?.nome}...`}
                                        value={inputValue}
                                        autoComplete="off"
                                        onChange={(e) => {
                                          setCitySearches((prev) => ({ ...prev, [sigla]: e.target.value }));
                                          setCityDropdownOpen((prev) => ({ ...prev, [sigla]: true }));
                                        }}
                                        onFocus={() =>
                                          setCityDropdownOpen((prev) => ({ ...prev, [sigla]: true }))
                                        }
                                        onBlur={() =>
                                          setTimeout(
                                            () => setCityDropdownOpen((prev) => ({ ...prev, [sigla]: false })),
                                            150
                                          )
                                        }
                                      />
                                      {isDropdownOpen && inputValue.length > 0 && suggestions.length > 0 && (
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "calc(100% + 4px)",
                                            left: 0,
                                            right: 0,
                                            backgroundColor: "#fff",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "10px",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                                            zIndex: 20,
                                            overflow: "hidden",
                                          }}
                                        >
                                          {suggestions.map((city) => (
                                            <button
                                              key={city.id}
                                              type="button"
                                              onMouseDown={() => {
                                                toggleCitySelection(city.nome);
                                                setCitySearches((prev) => ({ ...prev, [sigla]: "" }));
                                                setCityDropdownOpen((prev) => ({ ...prev, [sigla]: false }));
                                              }}
                                              style={{
                                                display: "block",
                                                width: "100%",
                                                textAlign: "left",
                                                padding: "9px 14px",
                                                fontSize: "0.85rem",
                                                background: "none",
                                                border: "none",
                                                borderBottom: "1px solid #f1f5f9",
                                                cursor: "pointer",
                                                color: "#1f2937",
                                              }}
                                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                              {city.nome}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Tags das cidades selecionadas */}
                                  {selectedCities.length > 0 && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                      {selectedCities.map((cityName) => (
                                        <span
                                          key={cityName}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            padding: "4px 10px",
                                            borderRadius: "999px",
                                            backgroundColor: "#f0fdf4",
                                            border: "1px solid #bbf7d0",
                                            fontSize: "0.8rem",
                                            fontWeight: 600,
                                            color: "#166534",
                                          }}
                                        >
                                          {cityName}
                                          <button
                                            type="button"
                                            onClick={() => toggleCitySelection(cityName)}
                                            style={{
                                              background: "none",
                                              border: "none",
                                              cursor: "pointer",
                                              color: "#166534",
                                              padding: 0,
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <FiX size={12} />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sessão 2: Precificação e Urgência */}
          {step === 2 && (
            <div className="fade-in">
              <h3
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "1.25rem",
                  marginBottom: "1.5rem",
                }}
              >
                <FiDollarSign color="#237e22" /> Como quer negociar?
              </h3>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                <div
                  className="form-group"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <label htmlFor="priceCondition" style={{ fontWeight: "600" }}>
                    Preço (BRL)
                  </label>
                  <input
                    id="priceCondition"
                    name="priceCondition"
                    type="text"
                    placeholder="Ex: R$ 50,00"
                    className="input"
                    value={formData.priceCondition}
                    onChange={handlePriceChange}
                    autoFocus
                    style={{
                      backgroundColor: "var(--color-background-alt, #fafafa)",
                    }}
                  />
                  <small style={{ color: "var(--color-text-muted)" }}>
                    Digite apenas números. Este valor aparecerá formatado na sua
                    listagem.
                  </small>
                </div>

                <div
                  className="form-group"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <label style={{ fontWeight: "600" }}>
                    Destaque de Urgência no Lote
                  </label>

                  <label
                    htmlFor="hasUrgency"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1.5rem",
                      borderRadius: "0.75rem",
                      border: formData.hasUrgency
                        ? "2px solid #ea580c"
                        : "1px solid var(--color-border, #cbd5e1)",
                      backgroundColor: formData.hasUrgency
                        ? "#fff7ed"
                        : "white",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <input
                      id="hasUrgency"
                      name="hasUrgency"
                      type="checkbox"
                      checked={formData.hasUrgency}
                      onChange={handleChange}
                      style={{
                        width: "1.5rem",
                        height: "1.5rem",
                        accentColor: "#ea580c",
                      }}
                    />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{
                          fontWeight: "600",
                          color: formData.hasUrgency ? "#c2410c" : "inherit",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          fontSize: "1.1rem",
                        }}
                      >
                        <FiClock /> Tratar como Validade Próxima / Lote de
                        queima
                      </span>
                      <span
                        style={{
                          color: formData.hasUrgency
                            ? "#ea580c"
                            : "var(--color-text-muted)",
                          fontSize: "0.9rem",
                          marginTop: "0.25rem",
                        }}
                      >
                        Ao marcar essa opção, o sistema adicionará selagens
                        visuais laranjas a este produto destacando urgência para
                        os compradores!
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Sessão 3: Revisão Final */}
          {step === 3 && (
            <div className="fade-in">
              <h3
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "1.25rem",
                  marginBottom: "1.5rem",
                }}
              >
                <FiLayers color="#237e22" /> Revise seu Catálogo
              </h3>

              <div style={{ display: "grid", gap: "1.5rem" }}>
                <div
                  style={{
                    padding: "1.5rem",
                    borderRadius: "0.75rem",
                    backgroundColor: "#f8fafc",
                    border: "1px solid var(--color-border, #cbd5e1)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--color-text-muted)",
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "600",
                    }}
                  >
                    Conteúdo Principal
                  </span>

                  <div
                    style={{
                      marginTop: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    {formData.media.length > 0 ? (
                      <img
                        src={resolveMediaPreviewSrc(formData.media[0])}
                        alt="Preview Capa"
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "0.5rem",
                          border: "1px solid var(--color-border)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "0.5rem",
                          backgroundColor: "#e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                        }}
                      >
                        <FiImage size={24} />
                      </div>
                    )}
                    <div>
                      <h4
                        style={{
                          margin: "0 0 0.25rem 0",
                          fontSize: "1.1rem",
                          color: "#0f172a",
                        }}
                      >
                        {formData.title || "Sem título"}
                      </h4>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#64748b",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formData.description || "Sem descrição",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {formData.coverageStates.length > 0 && (
                  <div
                    style={{
                      padding: "1.25rem",
                      borderRadius: "0.75rem",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <span
                      style={{
                        color: "#166534",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiMapPin size={14} /> Abrangência geográfica
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.75rem" }}>
                      {formData.coverageStates.map((sigla) => {
                        const stateInfo = ibgeStates.find((s) => s.sigla === sigla);
                        const cities = formData.coverageCities.filter((c) =>
                          (ibgeCitiesCache[sigla] || []).some((city) => city.nome === c)
                        );
                        return (
                          <div key={sigla}>
                            <p style={{ margin: "0 0 4px 0", fontWeight: 700, fontSize: "0.85rem", color: "#166534" }}>
                              {stateInfo?.nome || sigla}
                            </p>
                            {cities.length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                {cities.map((c) => (
                                  <span
                                    key={c}
                                    style={{
                                      padding: "2px 10px",
                                      borderRadius: "999px",
                                      backgroundColor: "#166534",
                                      color: "#fff",
                                      fontSize: "0.76rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {c}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b7280" }}>
                                Todo o estado
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      padding: "1.25rem",
                      borderRadius: "0.75rem",
                      backgroundColor: "white",
                      border: "1px solid var(--color-border, #cbd5e1)",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--color-text-muted)",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiDollarSign /> Preço / Condição
                    </span>
                    <p
                      style={{
                        margin: "0.5rem 0 0 0",
                        fontWeight: "600",
                        fontSize: "1.2rem",
                        color: "#237e22",
                      }}
                    >
                      {formData.priceCondition || "Não informado"}
                    </p>
                    <p
                      style={{
                        margin: "0.4rem 0 0 0",
                        color: "#475569",
                        fontSize: "0.92rem",
                      }}
                    >
                      Categoria: {selectedCategoryName}
                    </p>
                  </div>

                  <div
                    style={{
                      padding: "1.25rem",
                      borderRadius: "0.75rem",
                      backgroundColor: formData.hasUrgency
                        ? "#fff7ed"
                        : "white",
                      border: formData.hasUrgency
                        ? "1px solid #fed7aa"
                        : "1px solid var(--color-border, #cbd5e1)",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--color-text-muted)",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiClock /> Status de Urgência
                    </span>
                    <p
                      style={{
                        margin: "0.5rem 0 0 0",
                        fontWeight: "600",
                        fontSize: "1.1rem",
                        color: formData.hasUrgency ? "#ea580c" : "#64748b",
                      }}
                    >
                      {formData.hasUrgency
                        ? "Validade Próxima Ativada"
                        : "Lote Padrão"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: step === 1 ? "flex-end" : "space-between",
            marginTop: "1rem",
            paddingTop: "2rem",
          }}
        >
          {step > 1 && (
            <button
              type="button"
              className="button button--ghost"
              onClick={prevStep}
              style={{
                padding: "0.875rem 1.5rem",
                borderRadius: "0.5rem",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FiChevronLeft size={20} /> Etapa Anterior
            </button>
          )}

          {step < 3 ? (
            <button
              type="button"
              className="button button--primary"
              onClick={nextStep}
              disabled={!isStep1Valid && step === 1}
              style={{
                padding: "0.875rem 2.5rem",
                borderRadius: "0.5rem",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                opacity: !isStep1Valid && step === 1 ? 0.5 : 1,
                cursor: !isStep1Valid && step === 1 ? "not-allowed" : "pointer",
              }}
            >
              Avançar <FiChevronRight size={20} />
            </button>
          ) : (
            <button
              type="button"
              className="button button--primary fade-in"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: "0.875rem 2.5rem",
                borderRadius: "0.5rem",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.75rem",
                minWidth: "200px",
                justifyContent: "center",
                backgroundColor: "#16a34a", // Verde pra finalizar
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 4px 6px -1px rgba(22, 163, 74, 0.4)",
              }}
            >
              <FiSave size={20} />{" "}
              {loading ? "Preparando Lote..." : "Publicar Meu Catálogo"}
            </button>
          )}
        </div>
      </div>

      <ResponsiveModal
        isOpen={isCropOpen}
        onClose={() => {
          if (!uploading) closeCropper();
        }}
        title="Recortar imagem"
        subtitle="produto"
        size="lg"
        primaryAction={{
          label: uploading ? "Enviando..." : "Confirmar recorte",
          onClick: confirmCropAndUpload,
          disabled: uploading,
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--color-text-muted)",
            fontSize: "0.95rem",
          }}
        >
          Ajuste o enquadramento para um corte quadrado antes do envio.
        </p>

        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "0.75rem",
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "250px",
              backgroundColor: "#0f172a",
            }}
          >
            <Cropper
              image={selectedImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="horizontal-cover"
            />
          </div>

          <div
            style={{
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            <label
              htmlFor="zoom-control"
              style={{
                fontWeight: "600",
                fontSize: "0.9rem",
                color: "var(--color-text-muted)",
              }}
            >
              Zoom do recorte
            </label>
            <input
              id="zoom-control"
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default NewProductPage;
